<template>
  <main class="page-companies-show py-5">
    <div class="container" v-if="company">
      <div class="row no-gutters border-bottom mb-5">
        <div class="col-6 pb-3">
          <div class="mb-4 d-flex align-items-end">
            <div class="company-logo shadow rounded mr-3 d-flex align-items-center justify-content-center">
              <img v-if="company.logo" src="@/assets/images/logo.png" :alt="company.name" draggable="false" class="mw-100 mh-100" />
              <BlockWithInitials v-else :name="company.name" />
            </div>
            <PageTitle>
              {{ company.name }}
            </PageTitle>
          </div>
          <p class="mb-1">
            <strong>P.IVA:</strong> {{company.vat}}
          </p>
          <p v-if="company.consultant" class="mb-2">
            <strong>Supervisor:</strong>
            {{ company.consultant.name }} {{ company.consultant.surname }}
          </p>
        </div>
        <div class="col-6 text-right">
          <router-link class="btn btn-outline-primary mb-1" :to="{ name: 'companies-edit' }">Modifica</router-link>
          <router-link v-if="canNavigateToCompany" class="btn btn-primary mb-1 ml-3" :to="{ name: 'operative-company', params: { id: company.id } }">Vai all'azienda</router-link>
        </div>
      </div>

      <!-- Users -->
      <template v-if="(userIsContextCompanyManager || userIsContextCompanyConsultant || userIsAdmin)">
        <div class="d-flex align-items-center justify-content-between mb-4">
          <h2 class="text-primary">Utenti</h2>
          <button class="btn btn-outline-primary mb-4" @click="addUserModal.show()" v-if="canInviteUsers">
            <i class="fas fa-user-plus mr-1"></i>
            Aggiungi nuovo
          </button>

          <BModal title="Invita" dialog-class="modal--type-dialog" id="invite-company-modal" ref="invite-company-modal" hide-footer centered scrollable>
            <p>
              Invita un utente sull'azienda {{company.name}}
              <br />
              Deve accettare il tuo invito per poter accedere alla piattaforma
            </p>
            <ReservationForm :user="{ company: company.id }" :roles="rolesAvailable" @success="onReservedUser" />
          </BModal>
        </div>
        <div v-if="users.length > 0" class="row">
          <div v-for="user in users" :key="user.id" class="col-lg-4 pb-4">
            <LawaCard class="h-100">
              <h4 class="mb-4" :class="{ 'text-muted': !user.active }">
                {{user.active ? user.name + ' ' + user.surname : user.email}}
              </h4>
              <p class="mb-1">
                <strong>Ruolo: </strong>
                <span class="text-capitalize">
                  {{ user.role }}
                </span>
              </p>
              <p class="mb-1">
                <strong class="text-dark">Stato: </strong>

                <i :class="[ !user.active ? 'fas fa-hourglass-half text-warning' : 'fas fa-check text-success' ]"></i>
                {{user.active ? 'Attivo dal ' + user.activationDate : 'In attesa di accettazione'}}
              </p>
              <p class="mb-1">
                <strong>Email: </strong>
                <span>
                  {{ user.email }}
                </span>
              </p>
              <template #footer>
                <AwlConfirm
                  v-if="!user.active"
                  class="btn btn-block text-left"
                  status='outline-danger btn'
                  title='Elimina invito'
                  @confirm="deleteReservation(user)"
                >
                  Sei sicuro di voler eliminare l'invito per l'utente?
                </AwlConfirm>
                <AwlConfirm
                  v-else
                  class="btn text-left"
                  status='outline-danger btn'
                  title='Elimina'
                  @confirm="deleteUserForCompany(user)"
                >
                  Sei sicuro di voler eliminare l'utente {{ user.name }} {{ user.surname }} per questa azienda?
                </AwlConfirm>
                <button class="btn btn-block btn-primary align-self-center m-0" @click="reserve(user)" v-if="!user.active">
                  <i class="fas fa-share mr-1"></i>
                  Rimanda invito
                </button>
              </template>
            </LawaCard>
          </div>
        </div>
        <AlertFeedback v-else status="info">Non sono presenti utenti associati a questa azienda</AlertFeedback>
      </template>

      <!-- Modules -->
      <div v-if="(userIsContextCompanyManager || userIsContextCompanyConsultant || userIsAdmin) && modules.length > 0" class="mt-5">
        <div class="d-flex align-items-center justify-content-between mb-4">
          <h2 class="text-primary">Moduli</h2>
        </div>

        <div class="row">
          <div v-for="module in modulesElaborated" :key="module.code" class="col-md-6 mb-4">
            <div class="module rounded p-3" :class="{ 'disabled': !module.enabled }">
              <div class="d-flex align-items-center mb-2">
                <b-form-checkbox v-model="module.enabled" name="check-button" :disabled="!userCanEditModules" switch :key="module.code" @change="updateModule(module.code, module.enabled)" class="mr-3 mb-2" />

                <p class="h3">{{ module.name }}</p>
              </div>

              <p class="mb-0">{{ module.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>

import $api from '@/libs/Api/services/api.js';
import { BModal, BFormCheckbox } from 'bootstrap-vue';
import operativeContextRolesMixin from '@/mixins/operativeContextRoles.js';

export default {
  mixins: [operativeContextRolesMixin],
  components: {
    BModal,
    BFormCheckbox,
    LawaCard: () => import('@/views/components/Layout/Card/LawaCard.vue'),
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    ReservationForm: () => import('@/views/components/Account/ReservationForm.vue'),
    AlertFeedback: () => import('@/libs/Feedback/components/Alert.vue'),
    BlockWithInitials: () => import('@/views/components/Ui/BlockWithInitials.vue'),
    AwlConfirm: () => import('@/libs/Modals/components/AwlConfirm'),
  },
  data () {
    return {
      company: null,
      modules: [],
      checked: null,
    };
  },
  computed: {
    canInviteUsers () {
      return this.$oauth2.me.admin ? true : this.$oauth2.me?.canInviteUsers;
    },
    userCanEditModules () {
      return this.$oauth2.me.admin ? true : this.$oauth2.me?.canEditModules;
    },
    rolesAvailable () {
      if (!this.company?.hasBeenSetup) {
        return ['owner'];
      }
      return ['owner', 'manager', 'visitor'];
    },
    users () {
      return this.company?.users || [];
    },
    addUserModal () {
      return this.$refs['invite-company-modal'];
    },
    canNavigateToCompany () {
      return (this.$oauth2.me && (this.$oauth2.me.companies?.length || 0) >= 1) || this.$oauth2.me?.admin;
    },
    companyModules () {
      return this.company?.modules || [];
    },
    companyModulesCodes () {
      const modulesEnabled = {};

      this.companyModules.forEach(m => {
        modulesEnabled[m.code] = true;
      });

      return modulesEnabled;
    },
    modulesElaborated () {
      return this.modules.map(m => {
        m.enabled = !!this.companyModulesCodes[m.code];

        return m;
      });
    },
  },
  methods: {
    deleteUserForCompany (user) {
      this.$api.deleteCompanyUser(this.$route.params.id, user.id)
        .then(() => {
          this.$api.company(this.$route.params.id)
            .then((res) => {
              this.company = res.data;
              this.makeToast('success', 'Utente eliminato', 'Utente correttamente eliminato');
            });
        });
    },
    deleteReservation (user) {
      this.$api.deleteUserReservation(user.reservationId)
        .then(() => {
          this.$api.company(this.$route.params.id)
            .then((res) => {
              this.company = res.data;
              this.makeToast('success', 'Invito eliminato', 'Invito correttamente eliminato');
            });
        })
        .catch(err => {
          this.$log.debug(err);
        });
    },
    reserve (user) {
      this.$api.reserveUser({ ...user, company: this.company.id }).then(() => {
        this.makeToast('success', 'Invito inviato', `Email correttamente inviata a ${user.email}`);
      }).catch(err => {
        this.$log.debug(err);
      });
    },
    makeToast (variant = null, title = '', content = '') {
      this.$bvToast.toast(content, {
        title: title,
        toaster: 'b-toaster-top-right',
        solid: true,
        variant: variant,
        appendToast: true,
      });
    },
    onConfirm (event, row) {
      // azione di enable / disabled
    },
    onReservedUser (user) {
      this.$api.company(this.$route.params.id)
        .then(res => {
          this.company = res?.data || null;
          this.addUserModal.hide();
        })
        .catch(err => {
          this.$log.error(err);
        })
      ;
    },
    updateModule (code, currentState) {
      const endpoint = currentState ? this.$api.disableModuleForCompany : this.$api.enableModuleForCompany;
      endpoint(this.$route.params.id, code)
        .then(res => {
          this.company = res?.data || null;
        })
      ;
    },
  },
  async beforeRouteEnter (to, from, next) {
    try {
      const modulesResponse = await $api.listModules();
      const companyResponse = await $api.company(to.params.id);

      return next(vm => {
        vm.modules = modulesResponse?.data?.modules || [];
        vm.company = companyResponse?.data || null;
        if (companyResponse?.data?.role === 'consultant' && !vm.userIsContextCompanyConsultant) {
          vm.$oauth2.getMe(true);
        }
      });
    } catch (error) {
      return next(vm => vm.$log.error(error));
    }
  },
};

</script>

<style lang="scss">

$company-logo-square-size: 85px;

.page-companies-show {
  .awl-card {
    &.awl-card-users {
      position: relative;
    }
  }

  .company-logo {
    height: $company-logo-square-size;
    padding: .75rem;
    width: $company-logo-square-size;

    img {
      object-fit: contain;
    }
  }

  .module {
    background: rgba($primary, .1);
    transition: all .2s;

    &.disabled {
      background: rgba(#7C8489, .05);
    }
  }

  .custom-switch {
    height: 27px;
    width: 56px;

    .custom-control-label {
      cursor: pointer;
      &::before {
        background: #D0D4D7;
        border-radius: 100px;
        border-width: 0;
        height: 27px;
        top: 0;
        width: 56px;
      }

      &::after {
        background: #fff;
        border-radius: 100px;
        height: 21px;
        left: calc(-2.25rem + 3px);
        top: 3px;
        width: 21px;
      }
    }

    .custom-control-input {
      &:focus ~ .custom-control-label::before {
        box-shadow: none;
      }

      &:checked ~ {
        .custom-control-label::before {
          background: $primary;
        }

        .custom-control-label::after {
          transform: translateX(29px);
        }
      }
    }
  }
}

</style>
